import { IRecommendedGoalsBySpecializedMajors } from '../../cache/learner/recommendedGoalsBySpecializedMajorsReactiveVar';
import {
  getJobTitleBasedOnAcademicProgramRefetch,
  IJobTitle,
} from '../../operations/query/academicPrograms/useGetJobTitleBasedOnAcademicProgram';
import { getAcademicProgramsRefetch } from '../../operations/query/students/useGetAcademicPrograms/useGetAcademicPrograms';
import {
  getStudentAcademicProgramsRefetch,
  IAcademicProgram,
} from '../../operations/query/students/useGetStudentAcademicPrograms.ts/useGetStudentAcademicPrograms';

export interface IFetchRecommendedGoalsParams {
  studentId: string;
  defaultAcademicProgramId?: string;
  allowPartial?: boolean;
}

const mapJobTitleToMatch = (jobTitle: IJobTitle): IRecommendedGoalsBySpecializedMajors['match'][number] => ({
  name: jobTitle.name,
  match: jobTitle.match,
  jobTitleId: jobTitle.id,
});

const fetchJobTitlesForAcademicProgram = async (
  academicProgram: IAcademicProgram,
): Promise<IRecommendedGoalsBySpecializedMajors | null> => {
  try {
    const { data, error } = await getJobTitleBasedOnAcademicProgramRefetch(academicProgram.id);

    if (error || !data?.getJobTitleBasedOnAcademicProgram) {
      return null;
    }

    return {
      academicProgramId: academicProgram.id,
      academicProgramName: academicProgram.name,
      match: data.getJobTitleBasedOnAcademicProgram.map(mapJobTitleToMatch),
    };
  } catch {
    return null;
  }
};

const withDefaultAcademicProgram = async (
  studentAcademicPrograms: IAcademicProgram[],
  defaultAcademicProgramId?: string,
  allowPartial = false,
): Promise<IAcademicProgram[]> => {
  const isAlreadyIncluded = studentAcademicPrograms.some(({ id }) => id === defaultAcademicProgramId);

  if (!defaultAcademicProgramId || !studentAcademicPrograms.length || isAlreadyIncluded) {
    return studentAcademicPrograms;
  }

  try {
    const { data } = await getAcademicProgramsRefetch();
    const defaultAcademicProgram = data?.getAcademicPrograms?.find(({ id }) => id === defaultAcademicProgramId);

    if (!defaultAcademicProgram) {
      return studentAcademicPrograms;
    }

    return [...studentAcademicPrograms, { id: defaultAcademicProgram.id, name: defaultAcademicProgram.name }];
  } catch (error) {
    if (allowPartial) {
      return studentAcademicPrograms;
    }

    throw error;
  }
};

let latestRequestId = 0;

export const fetchRecommendedGoalsBySpecializedMajors = async ({
  studentId,
  defaultAcademicProgramId,
  allowPartial = false,
}: IFetchRecommendedGoalsParams): Promise<IRecommendedGoalsBySpecializedMajors[] | null> => {
  if (!studentId) {
    return null;
  }

  latestRequestId += 1;
  const requestId = latestRequestId;

  try {
    const { data, error } = await getStudentAcademicProgramsRefetch(studentId);

    if (error || !data?.getStudentAcademicPrograms) {
      return null;
    }

    const academicPrograms = await withDefaultAcademicProgram(
      data.getStudentAcademicPrograms,
      defaultAcademicProgramId,
      allowPartial,
    );
    const recommendedGoals = await Promise.all(academicPrograms.map(fetchJobTitlesForAcademicProgram));

    if (requestId !== latestRequestId) {
      return null;
    }

    if (allowPartial) {
      return recommendedGoals.filter((goal): goal is IRecommendedGoalsBySpecializedMajors => goal !== null);
    }

    if (recommendedGoals.includes(null)) {
      return null;
    }

    return recommendedGoals as IRecommendedGoalsBySpecializedMajors[];
  } catch {
    return null;
  }
};
